<template>
  <div class="users-list">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col">
                <h5 class="mb-0">{{ $t('users.title') }}</h5>
              </div>
              <div class="col-auto">
                
                <button 
                  v-if="canCreateUser"
                  type="button" 
                  class="btn btn-primary"
                  @click="$router.push('/users/create')"
                >
                  <i class="ti ti-plus me-1"></i>
                  {{ $t('users.addUser') }}
                </button>
              </div>
            </div>
          </div>
          
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            
            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <i class="ti ti-alert-circle me-2"></i>
              {{ error }}
            </div>
            
            <!-- Users Table -->
            <div v-else>
              <div class="table-responsive" v-if="users.length > 0">
                <table class="table table-hover align-middle resizable-table">
                  <thead class="table-light">
                    <tr>
                      <th :style="{ width: columnWidths[0] }" class="fw-normal position-relative">
                        {{ $t('users.fields.name') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 0)"></div>
                      </th>
                      <th :style="{ width: columnWidths[1] }" class="fw-normal position-relative">
                        {{ $t('users.fields.email') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 1)"></div>
                      </th>
                      <th :style="{ width: columnWidths[2] }" class="fw-normal position-relative">
                        {{ $t('users.fields.phone') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 2)"></div>
                      </th>
                      <th :style="{ width: columnWidths[3] }" class="fw-normal position-relative">
                        {{ $t('users.fields.emailConfirmed') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 3)"></div>
                      </th>
                      <th :style="{ width: columnWidths[4] }" class="fw-normal position-relative">
                        {{ $t('users.fields.status') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 4)"></div>
                      </th>
                      <th :style="{ width: columnWidths[5] }" class="text-center fw-normal position-relative">
                        {{ $t('common.actionsLabel') }}
                        <div class="column-resizer" @mousedown.prevent="startResize($event, 5)"></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in users" :key="user.id">
                      <td class="text-truncate" style="max-width: 220px;">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <div class="mb-0">{{ user.displayUserName || user.userName }}</div>
                            <small class="text-muted">{{ user.name || user.surname }}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="text-truncate">{{ user.email || '-' }}</span>
                      </td>
                      <td>
                        <span class="text-truncate">{{ user.phoneNumber || '-' }}</span>
                      </td>
                      <td>
                        <span 
                          class="badge" 
                          :class="user.emailConfirmed ? 'bg-success' : 'bg-warning'"
                        >
                          {{ user.emailConfirmed ? $t('users.emailStatus.confirmed') : $t('users.emailStatus.unconfirmed') }}
                        </span>
                      </td>
                      <td>
                        <span 
                          class="badge" 
                          :class="user.isActive ? 'bg-success' : 'bg-danger'"
                        >
                          {{ user.isActive ? $t('users.status.active') : $t('users.status.inactive') }}
                        </span>
                      </td>
                      <td>
                        <div class="d-flex justify-content-center gap-2">
                          <button 
                            v-if="canEditUser"
                            type="button" 
                            class="btn btn-outline-primary btn-action"
                            @click="editUser(user.id)"
                            :title="$t('common.edit')"
                          >
                            <i class="ti ti-edit"></i>
                          </button>
                          
                          <button 
                            v-if="canManagePermissions"
                            type="button" 
                            class="btn btn-outline-info btn-action"
                            @click="editPermissions(user.id)"
                            :title="$t('users.editPermissions')"
                          >
                            <i class="ti ti-shield-lock"></i>
                          </button>
                          
                          <button 
                            v-if="canDeleteUser && !isCurrentUser(user)"
                            type="button" 
                            class="btn btn-outline-danger btn-action"
                            @click="deleteUser(user)"
                            :title="$t('common.delete')"
                          >
                            <i class="ti ti-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <!-- Empty State -->
              <div v-else class="text-center py-4">
                <div class="mb-3">
                  <i class="ti ti-users text-muted" style="font-size: 3rem;"></i>
                </div>
                <h6 class="text-muted">{{ $t('users.noUsers') }}</h6>
                <p class="text-muted">{{ $t('users.noUsersDescription') }}</p>
                <button 
                  v-if="canCreateUser"
                  type="button" 
                  class="btn btn-primary"
                  @click="$router.push('/users/create')"
                >
                  <i class="ti ti-plus me-1"></i>
                  {{ $t('users.addFirstUser') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- User Permissions Modal -->
  <UserPermissionsModal :userId="selectedUserId" ref="permissionsModal" @error="handlePermissionError" />
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { usePermissions } from '@/composables/usePermissions';
import { useColumnResize } from '@/composables/useColumnResize';
import userService from '@/services/userService';
import UserPermissionsModal from '@/components/UserPermissionsModal.vue';
import Swal from 'sweetalert2';
import { Modal } from 'bootstrap';

const { t } = useI18n();

// Column definitions for resizing
const columns = [
  { width: 'auto' },   // Name
  { width: 'auto' },   // Email
  { width: 'auto' },   // Phone
  { width: 'auto' },   // Email Confirmed
  { width: 'auto' },   // Status
  { width: '200px' }   // Actions
];

// Initialize column resizing
const { columnWidths, startResize } = useColumnResize(columns, 'userList_columnWidths');
const router = useRouter();
const store = useStore();
const { hasPermission } = usePermissions();

const users = ref([]);
const loading = ref(false);
const error = ref('');
const selectedUserId = ref(null);
const permissionsModal = ref(null);

const currentUser = computed(() => store.getters['auth/currentUser']);

const canCreateUser = computed(() => hasPermission('Supplier.Users.Create'));
const canEditUser = computed(() => hasPermission('Supplier.Users.Edit'));
const canDeleteUser = computed(() => hasPermission('Supplier.Users.Delete'));
const canManagePermissions = computed(() => hasPermission('Supplier.Users.ManagePermissions'));

const loadUsers = async () => {
  loading.value = true;
  error.value = '';
  
  try {
    const response = await userService.getTenantUsers();
    users.value = response || [];
  } catch (err) {
    console.error('Error loading users:', err);
    error.value = t('users.errors.loadFailed');
  } finally {
    loading.value = false;
  }
};

const editUser = (userId) => {
  router.push(`/users/edit/${userId}`);
};

const editPermissions = async (userId) => {
  selectedUserId.value = userId;
  
  // Wait for next tick to ensure userId is set
  await new Promise(resolve => setTimeout(resolve, 0));
  
  const modalElement = document.getElementById('userPermissionsModal');
  const modal = new Modal(modalElement);
  
  // Load permissions after modal is shown
  modalElement.addEventListener('shown.bs.modal', async () => {
    if (permissionsModal.value) {
      await permissionsModal.value.loadUserPermissions();
    }
  }, { once: true });
  
  modal.show();
};

const toggleUserStatus = async (user) => {
  const newStatus = !user.isActive;
  const action = newStatus ? t('users.activate') : t('users.deactivate');
  
  const result = await Swal.fire({
    title: t('common.actions.confirm'),
    text: t('users.confirmStatusChange', { action, name: user.displayUserName || user.userName }),
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: t('common.actions.confirm'),
    cancelButtonText: t('common.actions.cancel')
  });

  if (result.isConfirmed) {
    try {
      await userService.toggleUserStatus(user.id, newStatus);
      user.isActive = newStatus;
      
      Swal.fire({
        title: t('common.actions.success'),
        text: t('users.statusChangedSuccess'),
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error toggling user status:', err);
      Swal.fire({
        title: t('common.actions.error'),
        text: t('users.errors.statusChangeFailed'),
        icon: 'error'
      });
    }
  }
};

const deleteUser = async (user) => {
  const result = await Swal.fire({
    title: t('common.actions.confirm'),
    text: t('users.confirmDelete', { name: user.displayUserName || user.userName }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: t('common.actions.delete'),
    cancelButtonText: t('common.actions.cancel')
  });

  if (result.isConfirmed) {
    try {
      await userService.deleteUser(user.id);
      users.value = users.value.filter(u => u.id !== user.id);
      
      Swal.fire({
        title: t('common.actions.success'),
        text: t('users.deleteSuccess'),
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error deleting user:', err);
      
      // Get specific error message from API response
      let errorMessage = t('users.errors.deleteFailed');
      
      if (err && err.response && err.response.data && err.response.data.error) {
        const apiError = err.response.data.error;
        if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if (err && err.message) {
        errorMessage = err.message;
      }
      
      Swal.fire({
        title: t('common.actions.error'),
        text: errorMessage,
        icon: 'error'
      });
    }
  }
};

const isCurrentUser = (user) => {
  return currentUser.value && currentUser.value.id === user.id;
};

const handlePermissionError = (error) => {
  let errorMessage = t('users.permissions.operationFailed');
  
  // Check if error has the specific API format
  if (error && error.response && error.response.data && error.response.data.error) {
    const apiError = error.response.data.error;
    if (apiError.message) {
      errorMessage = apiError.message;
    }
  } else if (error && error.message) {
    errorMessage = error.message;
  }
  
  Swal.fire({
    title: t('common.actions.error'),
    text: errorMessage,
    icon: 'error'
  });
};

onMounted(() => {
  loadUsers();
});
</script>

<style scoped>
.users-list {
  padding: 0;
}

.table th {
  border-top: none;
  font-weight: 600;
}

.avtar {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.avtar-s {
  width: 2rem;
  height: 2rem;
}

.bg-light-primary {
  background-color: rgba(var(--bs-primary-rgb), 0.1);
  color: var(--bs-primary);
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.btn-action {
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

/* Column resizing styles */
.resizable-table th {
  position: relative;
  overflow: hidden;
}

.column-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
}

.column-resizer:hover,
.column-resizer:active {
  background-color: #0d6efd;
}

.resizable-table th:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.resizable-table th:hover .column-resizer {
  background-color: rgba(13, 110, 253, 0.3);
}
</style> 